'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { constructQuery } from '@repo/shared/helpers';
import { Spinner } from '@repo/components';
import LogsTab from 'src/parts/tabs/LogsTab';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getEventLogs } from 'src/api/personal/eventDetail';
import { startOfDay, subMonths, endOfDay } from 'date-fns';
interface LogsSearch {
	from?: string;
	until?: string;
	page?: number;
	sort?: string;
}

export const Route = createFileRoute('/personal_/events/$eventId/logs')({
	validateSearch: (search: Record<string, unknown>): LogsSearch => {
		const now = new Date();
		return {
			from: (search.from as string) || startOfDay(subMonths(now, 1)).toISOString(),
			until: (search.until as string) || endOfDay(now).toISOString(),
			page: (search.page as number) || undefined,
			sort: search.sort as string | undefined,
		};
	},
	component: LogsPage,
});

function LogsPage() {
	const { eventId } = Route.useParams();
	const { from, until, page, sort } = Route.useSearch();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const hasAllEventsView = verifyPermission([PERMISSIONS.personalAllEventsView]);
	const asOwner = !hasAllEventsView;

	const query = constructQuery({
		from,
		until,
		page,
		sort: sort || 'timestamp:desc',
	});
	const {
		data: logs,
		isLoading,
		refetch: refetchLogs,
	} = useQuery({
		queryKey: ['personal-event-logs', eventId, asOwner, from, until, page, sort],
		queryFn: () => getEventLogs(eventId, query, asOwner),
	});

	const handleReload = () => refetchLogs();

	if (authLoading || isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	return <LogsTab showFilter={false} logType="EventLog" logs={logs} onReload={handleReload} />;
}
